import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ViewChild,
  Input,
  EventEmitter,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { SessionStorageService } from '../services/session-storage.service';
import { ModuleStatusService } from '../services/module-status.service';
import { UtilityAppsService } from '../shared-components/utility-apps/utility-apps.service';
import { RightBridgeApiService } from '../services/right-bridge-api.service';
import { SimpleLoadingComponent } from '../shared-components/simple-loading/simple-loading.component';
import { MarkdownModule, provideMarkdown } from 'ngx-markdown';
import { SpeechToTextComponent } from '../shared-components/speech-to-text/speech-to-text.component';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WarnDialogComponent } from '../warn-dialog/warn-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { webSocket } from 'rxjs/webSocket';
import { WebSocketSubject } from 'rxjs/internal/observable/dom/WebSocketSubject';
import { EnvironmentService } from '../services/environment.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

interface Message {
  NoteId: string;
  CreatedByUserId: string;
  CreateDate: Date;
  NoteText: string;
}

interface AuthConfig {
  Authorization: string;
  Prompt?: string;
  ProfileID?: string;
  'x-session'?: string;
}

@Component({
  selector: 'app-rb-chatbot',
  templateUrl: './rb-chatbot.component.html',
  styleUrls: ['./rb-chatbot.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MarkdownModule,
    ReactiveFormsModule,
    SpeechToTextComponent,
    SimpleLoadingComponent,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
  providers: [provideMarkdown()],
})
export class RbChatbotComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('speechToText', { static: false }) speechToText;
  @ViewChild('fileUpload', { static: false }) fileUpload: HTMLInputElement;

  private ss = inject(SessionStorageService);
  private envSvc = inject(EnvironmentService);
  dialog = inject(MatDialog);
  private uas = inject(UtilityAppsService);
  private snacky = inject(MatSnackBar);
  private rbs = inject(RightBridgeApiService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  modStatusSvc = inject(ModuleStatusService);

  aiUrl = '';
  rights = [];
  canSpeechToText = false;
  defaultUser;
  messages: Array<Message> = [];
  message = '';
  externalTriggerStop: any;
  capturedData = '';
  responseLoading = false;
  error = false;
  unsubscribe: Subject<any> = new Subject();
  profileId = '';
  subject: WebSocketSubject<any>;
  aiNoteCount = 0;
  formlyData = {};
  authAttempts = 0;
  pushAiMessage = '';
  voiceUsed = false;
  panelWasOpened = false;
  @Input() embedded = false;
  @Input() opened = false;
  @Output() closeEmitter = new EventEmitter();
  canUploadDoc: boolean = true;
  file: File | null = null;
  files: File[] = [];
  loadingFiles: string[] = [];
  fileAttachData: any = {};

  ngOnInit(): void {
    const env = this.envSvc.get();
    if (env?.aiBase) {
      this.aiUrl = this.embedded
        ? `${env.aiBase}/skill`
        : `${env.aiBase}/questionnaire`;
    }

    const globals = this.ss.get('globals');
    this.defaultUser = globals ? globals.user : null;
    this.rights = this.ss.get('rights');
    this.canSpeechToText = this.rights && this.rights.includes('SpeechToText');
    this.canUploadDoc =
      this.rights && this.rights.includes('UploadToChatbot') && !this.embedded;
    if (!this.embedded) {
      this.activatedRoute.params
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(({ profileId }) => {
          if (profileId) {
            this.profileId = profileId;
          }
        });
    }
    if (!this.embedded) {
      this.setupChatbot();
    }
  }

  setupChatbot(initMsg: string | any = '') {
    this.subject = webSocket({
      url: this.aiUrl,
      deserializer: msg => msg.data,
    });

    if (!this.ss.get('token')) {
      this.getAuth(initMsg);
    } else {
      const authConfig: AuthConfig = {
        Authorization: 'Bearer ' + this.ss.get('token'),
      };

      if (!this.embedded) {
        authConfig['Prompt'] = 'promptname';
      }

      const sendAuthConfig = () => {
        if (this.profileId) {
          authConfig['ProfileID'] = this.profileId;
        }

        if (
          this.ss.get('chatbotSession') &&
          this.ss.get('chatbotSession') !== 'Authentication failed' &&
          !this.ss.get('chatbotSession').toLowerCase().includes('error')
        ) {
          authConfig['x-session'] = this.ss.get('chatbotSession');
        }

        this.subject.next(authConfig);

        this.initializeChatbot(initMsg);
      };

      if (!this.embedded && !this.profileId) {
        this.responseLoading = true;
        this.rbs.newProfile(this.formlyData).subscribe(resp => {
          this.profileId = resp.results;
          sendAuthConfig();
        });
      } else {
        sendAuthConfig();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      Object.prototype.hasOwnProperty.call(changes, 'opened') &&
      changes.opened.currentValue === true &&
      !this.panelWasOpened
    ) {
      this.panelWasOpened = true;
    }
  }

  ngOnDestroy() {
    this.uas.appsList.set([]);
    this.unsubscribe.next(null);
    this.unsubscribe.complete();

    if (this.ss.get('chatbotSession')) {
      this.ss.remove('chatbotSession');
    }

    if (this.subject) {
      this.subject.complete();
      this.subject.unsubscribe();
    }
  }

  getAuth(initMsg: string | any = '') {
    this.rbs.getToken().subscribe(resp => {
      const authConfig: AuthConfig = {
        Authorization: 'Bearer ' + resp['access_token'],
      };

      if (!this.embedded) {
        authConfig['Prompt'] = 'promptname';
      }

      const sendAuthConfig = () => {
        if (this.profileId) {
          authConfig['ProfileID'] = this.profileId;
        }

        if (
          this.ss.get('chatbotSession') &&
          this.ss.get('chatbotSession') !== 'Authentication failed' &&
          !this.ss.get('chatbotSession').toLowerCase().includes('error')
        ) {
          authConfig['x-session'] = this.ss.get('chatbotSession');
        }

        if (resp['access_token']) {
          this.subject.next(authConfig);
          this.initializeChatbot(initMsg);
        }
      };

      if (!this.embedded && !this.profileId) {
        this.responseLoading = true;
        this.rbs.newProfile(this.formlyData).subscribe(resp => {
          this.profileId = resp.results;
          sendAuthConfig();
        });
      } else {
        sendAuthConfig();
      }
    });
  }

  initializeChatbot(initMsg = '') {
    this.subject.subscribe({
      next: msg => {
        if (!this.embedded) {
          this.responseLoading = true;
        }

        if (msg === 'Authentication failed' && this.authAttempts <= 3) {
          setTimeout(() => {
            this.subject.complete();
            this.getAuth();
            this.authAttempts += 1;
          }, 500 * this.authAttempts);
        }

        if (msg !== '' && !msg.includes('{')) {
          this.ss.set('chatbotSession', msg);
        }

        if (msg.includes('"Data":') && msg.includes('}')) {
          this.responseLoading = false;

          const msgData = JSON.parse(msg);

          this.capturedData = msgData.Data;

          this.formlyData = msgData.Profile;

          if (this.pushAiMessage !== msg) {
            this.messages.push({
              NoteId: this.aiNoteCount.toString(),
              CreatedByUserId: `rb-ai`,
              CreateDate: new Date(),
              NoteText: msgData.Chat,
            });
            this.scrollToBottom();
            this.aiNoteCount = this.aiNoteCount + 1;
            this.pushAiMessage = msg;
          }
        }

        if (this.embedded) {
          if (msg.includes('"Chat":') && msg.includes('}')) {
            this.responseLoading = false;

            const msgData = JSON.parse(msg);

            if (this.pushAiMessage !== msg) {
              this.messages.push({
                NoteId: this.aiNoteCount.toString(),
                CreatedByUserId: `rb-ai`,
                CreateDate: new Date(),
                NoteText: msgData.Chat,
              });
              this.scrollToBottom();
              this.aiNoteCount = this.aiNoteCount + 1;
              this.pushAiMessage = msg;
            }
          }
        }
      }, // Called whenever there is a message from the server.
      error: err => {
        console.warn('ERROR', err);
        this.error = true;

        if (this.subject) {
          this.subject.unsubscribe();
          this.getAuth();
        }
      }, // Called if at any point WebSocket API signals some kind of error.
      complete: () => {
        if (this.subject) {
          this.subject.unsubscribe();
        }
        console.warn(
          'Chat connection closed. If you did not expect to see this, there may be an issue with the connection.'
        );
        this.responseLoading = false;
      }, // Called when connection is closed (for whatever reason).
    });

    if (initMsg) {
      this.subject.next(initMsg);

      this.responseLoading = true;
    }
  }

  checkSubmit(ev: KeyboardEvent) {
    ev.preventDefault();

    if ((ev.code === 'Enter' || ev.code === 'NumpadEnter') && !ev.shiftKey) {
      if (this.message && this.message !== '<p></p>' && !this.responseLoading) {
        this.submitNote();
      }
    }
  }

  applySpeechToTextNote(ev: string) {
    this.message = ev;
  }

  applySpeechToTextError(ev: string) {
    if (ev !== 'reset') {
      this.snacky.open(ev, 'Close', {
        duration: 8000,
      });
    }
  }

  submitNote() {
    if (!this.loadingFiles.length) {
      if (
        this.speechToText &&
        this.speechToText.micButtonStatus !== 'disabled'
      ) {
        this.speechToText.startListening();
      }
      this.pushAiMessage = '';
      let convertedString = this.message;

      if (this.message.includes('\n')) {
        convertedString = this.message.replaceAll('\n', '<br/>');
      }

      const noteData = {
        Chat: convertedString,
      };
      if (Object.keys(this.fileAttachData).length) {
        Object.assign(noteData, { Documents: { ...this.fileAttachData } });
      }

      this.fileAttachData = {};
      this.files = [];

      this.messages.push({
        NoteId: Math.random().toString(),
        CreatedByUserId: this.defaultUser.id,
        CreateDate: new Date(),
        NoteText: convertedString,
      });
      if (this.subject) {
        this.subject.next(noteData);

        this.responseLoading = true;
      } else {
        this.setupChatbot(JSON.stringify(noteData));
      }

      this.message = '';
      this.scrollToBottom();
      document.getElementById('chatInput')?.focus();
    } else {
      this.snacky.open('File upload in progress.', 'Close', {
        duration: 5000,
      });
    }
  }

  scrollToBottom() {
    const chatContainer = document.querySelector('.chat-messages-container');
    const dataContainer = document.querySelector('.captured-data');

    if (chatContainer) {
      setTimeout(() => {
        const oldChatScrollPostion = chatContainer?.scrollHeight;

        chatContainer.scrollTo({
          top: oldChatScrollPostion,
          left: 0,
          behavior: 'smooth',
        });
      });
    }
    if (dataContainer) {
      setTimeout(() => {
        const oldDataScrollPostion = dataContainer?.scrollHeight;

        dataContainer.scrollTo({
          top: oldDataScrollPostion,
          left: 0,
          behavior: 'smooth',
        });
      });
    }
  }

  saveAndExit() {
    this.rbs.saveProfile(this.profileId, this.formlyData).subscribe(() => {
      this.router.navigate([`/case/rb/${this.profileId}/0`]);
      this.ss.remove('chatbotSession');
      if (this.subject) {
        this.subject.complete();
      }
    });
  }

  cancelChat() {
    const dialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Warning',
        content:
          'Are you sure you would like to cancel? All captured data will be lost.',
        confirm: 'Confirm & Exit',
        cancel: 'Continue Chatting',
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        this.ss.remove('chatbotSession');
        if (this.subject) {
          this.subject.complete();
        }

        if (this.profileId) {
          this.router.navigate([`/case/rb/${this.profileId}/0`]);
        } else {
          this.goToEmptyQuestionnaire();
        }
      }
    });
  }

  goToEmptyQuestionnaire() {
    const setApp = 'rb';

    this.modStatusSvc.apps.set(['rb']);
    this.ss.set('currentApp', setApp);
    this.rbs.setApp(setApp);

    this.rbs.newProfile({ 'Client.StartPage': setApp }, true).subscribe(x => {
      if (!this.rights?.includes('ReadOnly')) {
        this.router.navigate([`/case/${setApp}/${x.results}/0`]);
      } else {
        this.rbs.getQuestions(x.results).subscribe(data => {
          const profile = this.profileId ? this.profileId : this.getUniqueId(3);
          this.ss.set('noSave', {
            id: profile,
            profile: null,
            questionnaire: data.results.questionnaire,
          });

          this.router.navigate([`/case/${setApp}/${profile}/0`]);
        });
      }
    });
  }

  getUniqueId(parts: number): string {
    const stringArr = [];
    for (let i = 0; i < parts; i++) {
      const S4 = (((1 + Math.random()) * 0x10000) | 0)
        .toString(16)
        .substring(1);
      stringArr.push(S4);
    }
    return stringArr.join('-');
  }

  resetChat() {
    this.message = '';
    this.messages = [];
    this.capturedData = '';
    this.responseLoading = false;
    this.error = false;
    this.aiNoteCount = 0;
    this.formlyData = {};
    this.authAttempts = 0;
    this.pushAiMessage = '';
    this.files = [];
    this.loadingFiles = [];
    this.fileAttachData = {};

    if (this.ss.get('chatbotSession')) {
      this.ss.remove('chatbotSession');
    }

    if (this.subject) {
      this.subject = webSocket({
        url: this.aiUrl,
        deserializer: msg => msg.data,
      });
    }

    this.getAuth();
  }

  triggerClose() {
    this.closeEmitter.emit();
  }

  uploadFile(fileItem) {
    this.loadingFiles.push(fileItem.name);

    const formData = new FormData();
    formData.append('document', fileItem);

    this.rbs
      .uploadFileToProcess(formData, undefined, undefined, undefined, true)
      .subscribe({
        next: (res: string | null) => {
          try {
            Object.assign(this.fileAttachData, {
              [fileItem.name]: JSON.parse(res),
            });
          } catch (error) {
            console.error('Response data could not be read.');

            this.files = [
              ...this.files.filter(item => item.name !== fileItem.name),
            ];

            this.snacky.open('File upload failed.', 'Close', {
              duration: 8000,
            });
          }
          this.loadingFiles = this.loadingFiles.filter(
            loadFile => loadFile !== fileItem.name
          );
        },
        error: err => {
          console.error('Upload failed:', err);

          this.loadingFiles = this.loadingFiles.filter(
            loadFile => loadFile !== fileItem.name
          );
          this.files = [
            ...this.files.filter(item => item.name !== fileItem.name),
          ];

          this.snacky.open('File upload failed.', 'Close', {
            duration: 8000,
          });
        },
      });
  }

  addFiles(event: Event) {
    const fileUpload = event.target as HTMLInputElement;
    Array.from(fileUpload.files).forEach(fileItem => {
      this.uploadFile(fileItem);
    });
    this.files = [...Array.from(fileUpload.files), ...this.files];
    fileUpload.value = '';
  }

  removeFile(name) {
    this.files = [...this.files.filter(file => file.name !== name)];
    delete this.fileAttachData[name];
  }
}
